import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import HomePage from "@/pages/internal/home/HomePage";
import BriefingWizard from "./briefing/BriefingWizard";
import { useBriefing } from "./briefing/useBriefing";

/**
 * ImplementationEntry — gate for the implementation home.
 *
 * For an account admin whose guided briefing isn't completed yet, the new
 * BriefingWizard replaces the legacy 3-card dashboard. Everyone else (agents,
 * superadmins operating across accounts, completed accounts) gets the dashboard.
 *
 * The briefing query is only enabled for account admins so non-admin visits
 * don't get-or-create briefing rows.
 */
export default function ImplementationEntry() {
  const { clientId: paramId } = useParams();
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const clientId = paramId || user?.accountId;

  const isAccountAdmin =
    !!user && typeof isAdmin === "function" && isAdmin() && !isSuperAdmin?.();

  const { briefing, isLoading } = useBriefing(isAccountAdmin ? clientId : null);

  if (isAccountAdmin && isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show the wizard only for a genuinely-new account. An account already live
  // (working flow OR active channel) that never ran the briefing — e.g. set up
  // before the wizard shipped — keeps the dashboard instead of being forced
  // into onboarding. The backend persists that detection on the briefing
  // (status "completed", completedBy "system:*"), so this can never re-arm.
  const showWizard =
    isAccountAdmin &&
    !!briefing &&
    briefing.status !== "completed" &&
    !briefing.alreadyImplemented;

  if (showWizard) {
    return <BriefingWizard clientId={clientId} />;
  }

  // Completed briefing → the Home v2 front door, which replaced the legacy
  // implementation dashboard.
  //
  // Two dashboard-era companions were dropped with it. TourProvider existed so
  // the old dashboard's useTour() kept working, and its spotlight branches were
  // already inert. TutorialsLauncher was a floating launcher whose three
  // tutorials were hardcoded in the component and duplicated CMS articles that
  // already exist — the ask box and the editorial row now cover that ground
  // from a single source of truth. The per-user inbox tutorial is unaffected;
  // it runs from InboxTour on the inbox's first visit.
  return <HomePage />;
}
